import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SkeletonGrid } from "@/components/Skeleton";
import { MapPin, Landmark, Palette, Sparkles, Trees, UtensilsCrossed, Lock, Gift } from "lucide-react";

// Icon lookup — admin picks one of these keys when creating a new category.
const ICON_LOOKUP = {
  mappin: MapPin,
  landmark: Landmark,
  palette: Palette,
  sparkles: Sparkles,
  trees: Trees,
  utensils: UtensilsCrossed,
  gift: Gift,
};

function iconForCategory(cat, categories) {
  const match = categories.find((c) => c.k === cat || c.key === cat);
  const iconKey = match?.icon || "mappin";
  return ICON_LOOKUP[iconKey] || MapPin;
}

export default function WaymarksListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [cat, setCat] = useState("all");
  const [city, setCity] = useState("");
  const [categories, setCategories] = useState([]);

  // Load dynamic categories once so the filter chips stay in sync with admin-added ones.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/waymark-categories");
        setCategories(data.categories || []);
      } catch {
        setCategories([]);
      }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    (async () => {
      try {
        const params = {};
        if (cat !== "all") params.category = cat;
        if (city.trim()) params.city = city.trim();
        const { data } = await api.get("/waymarks", { params });
        setRows(data.waymarks || []);
      } catch (err) {
        setRows([]);
        setLoadError(
          err.response?.status
            ? `Couldn't load waymarks (${err.response.status}). Try again in a moment.`
            : "Couldn't reach the server. Check your connection and retry."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [cat, city]);

  return (
    <div className="py-8 space-y-8">
      <div>
        <div className="overline">Permanent pins · always open</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2">
          Way<span style={{ color: "#76B82A" }}>marks</span>
        </h1>
        <p className="text-[#F5F2E8]/70 mt-3 max-w-2xl">
          Hidden history, weird landmarks, and local secrets pinned on the map. Walk up to one
          and unlock the story behind it — some even pay out a perk from the sponsor on the corner.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {[{ key: "all", label: "All", icon: "mappin" }, ...categories].map((c) => {
          const Icon = ICON_LOOKUP[c.icon] || MapPin;
          const k = c.key;
          return (
            <button
              key={k}
              onClick={() => setCat(k)}
              data-testid={`waymarks-filter-${k}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-display uppercase tracking-wide text-sm transition ${
                cat === k
                  ? "text-[#76B82A] border-b-2 border-[#76B82A]"
                  : "text-[#F5F2E8]/60 hover:text-[#F5F2E8]"
              }`}
            >
              <Icon size={13} /> {c.label}
            </button>
          );
        })}
        <input
          className="loot-input ml-auto max-w-xs"
          placeholder="Filter by city…"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          data-testid="waymarks-city-filter"
        />
      </div>

      {loading ? (
        <SkeletonGrid count={6} />
      ) : rows.length === 0 ? (
        <div className="loot-card-flat p-12 text-center text-[#F5F2E8]/60" data-testid="waymarks-empty">
          No waymarks match this filter yet. Check back soon — we're adding new pins every week.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((w) => (
            <Link
              to={`/waymarks/${w.id}`}
              key={w.id}
              data-testid={`waymark-card-${w.id}`}
              className="loot-card p-5 block relative"
              style={w.is_sponsored ? {
                borderColor: "#D7A62A",
                background: "linear-gradient(135deg, rgba(215,166,42,0.06), rgba(15,15,15,1) 65%)",
              } : undefined}
            >
              {w.is_sponsored && (
                <span
                  className="absolute top-3 right-3 font-mono text-[10px] px-1.5 py-0.5 border"
                  style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
                >
                  <Gift size={10} className="inline mr-1" />PERK
                </span>
              )}
              <div className="flex items-center gap-2 text-[#76B82A]">
                {(() => {
                  const Icon = iconForCategory(w.category, categories.map((c) => ({ k: c.key, icon: c.icon })));
                  return <Icon size={14} />;
                })()}
                <span className="overline">{w.category || "waymark"}</span>
              </div>
              <h3 className="font-display text-2xl uppercase mt-3 tracking-tight">{w.title}</h3>
              <div className="mt-2 flex items-center gap-2 text-sm text-[#F5F2E8]/70">
                <MapPin size={14} /> {w.city}
              </div>
              <div className="mt-3 flex items-center gap-1 font-mono text-xs text-[#F5F2E8]/50">
                <Lock size={11} /> Walk up to unlock the story
              </div>
              {w.is_sponsored && w.sponsor?.business_name && (
                <div className="mt-3 font-mono text-[11px] text-[#D7A62A]/80">
                  Perk by {w.sponsor.business_name}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
